// inbox-data.js — live data layer for the AI Sales Bot Inbox.
//
// Ported from the proven live_chat_looker (src/App.jsx), adapted to:
//   • Create React App env vars (process.env.REACT_APP_* — NOT import.meta.env)
//   • the new UI's data shapes: CONVERSATIONS[], THREADS{}, PROFILE{}, STAFF[]
//
// READS conversations + users + staff every 5s via the Supabase REST API
// (anon key + RLS). WRITES are limited to users.escalation (human-takeover) and
// users.assigned_staff_* (reassign). Human replies are NEVER inserted here — they
// go through the Cloudflare Worker relay (/agent-send), which sends to WhatsApp
// and logs the message back to `conversations`.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const REFRESH_MS = 5000; // poll cadence
const MSG_LIMIT = 5000; // most-recent messages pulled per poll

const SUPABASE_URL = (process.env.REACT_APP_SUPABASE_URL || "").replace(/\/$/, "");
const ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "";
export const CONFIGURED = Boolean(SUPABASE_URL && ANON_KEY);

const WORKER_URL = (process.env.REACT_APP_WORKER_URL || "").replace(/\/$/, "");
const AGENT_SECRET = process.env.REACT_APP_AGENT_SECRET || "";
export const CAN_SEND = Boolean(WORKER_URL && AGENT_SECRET);

export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
export const ALLOWED_DOMAIN = "figment.live";

// ── Environment safety switch ───────────────────────────────────
// ENVIRONMENT=local → TEST MODE: only whitelisted numbers may be escalated or
// replied to, so we can never touch live customer chats while testing. Anything
// other than "local" (incl. "production" / unset) applies no filter.
export const ENVIRONMENT = (process.env.REACT_APP_ENVIRONMENT || "production")
    .trim().toLowerCase();
export const IS_LOCAL = ENVIRONMENT === "local";

const digitsOnly = (p) => String(p || "").replace(/\D/g, "");
export const WHITELIST_NUMBERS = (process.env.REACT_APP_WHITELIST_NUMBERS || "")
    .split(",").map(digitsOnly).filter(Boolean);

// True when a phone may be acted on (escalate / reply). Always true outside local
// mode; in local mode only whitelisted numbers qualify. Matched on digits only.
export function isAnswerable(phone) {
    if (!IS_LOCAL) return true;
    return WHITELIST_NUMBERS.includes(digitsOnly(phone));
}

export const BLOCKED_MSG =
    "Test mode (ENVIRONMENT=local): this number isn't whitelisted, so escalation and replies are disabled here to protect live chats.";

const headers = {
    apikey: ANON_KEY,
    Authorization: `Bearer ${ANON_KEY}`,
    Accept: "application/json",
};

async function supaGet(path) {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, { headers });
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
    return r.json();
}

// A lead is ACTIVE if their last genuine inbound was within this window (db/72 uses the same 7 days).
const ACTIVE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

// Resolve one user row's lead-activity fields. The effective state is
// COALESCE(manual override, 7-day derivation) — the same resolution the
// `v_lead_status` view does server-side, so the badge and the view agree.
//   activeLead              true | false | null   (null = UNKNOWN, never hidden by the filter)
//   activityOverride        "active" | "inactive" | null   (null = automatic)
//   activityIsManual        the override, not the 7-day rule, decided it
//   activityDerived         what the 7-day rule alone says (ignores the override)
//   activityOverrideReady   the activity_override column came back at all
// A column that isn't in the response is `undefined`, NOT null — that's how the
// fallback fetches (older DB / migration not run) are told apart from a real NULL.
// Absent must not mean inactive, so an absent last_inbound_at stays `null`.
function activityOf(u) {
    const derived = u.last_inbound_at === undefined
        ? null
        : (u.last_inbound_at
            ? (Date.now() - new Date(u.last_inbound_at).getTime()) <= ACTIVE_WINDOW_MS
            : false);
    const supported = u.activity_override !== undefined;
    const override = supported ? (u.activity_override || null) : null;
    return {
        activeLead: override === "active" ? true : override === "inactive" ? false : derived,
        activityOverride: override,
        activityIsManual: Boolean(override),
        activityDerived: derived,
        activityOverrideReady: supported,
    };
}

// Like supaGet but pages through ALL rows. PostgREST caps a single response at ~1000 rows,
// so a bare `users?select=…` silently drops everyone past the first 1000 → those conversations
// render with null source / "Unassigned" / null profile even though the DB row is correct.
// This pages via Range headers (1000 at a time) until a short page. Callers MUST pass a stable
// `&order=` (e.g. phonenumber) so pages don't overlap or skip. (fix: +usersallpages)
async function supaGetAll(path) {
    const PAGE = 1000;
    let from = 0;
    let out = [];
    for (;;) {
        const r = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
            headers: { ...headers, Range: `${from}-${from + PAGE - 1}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
        const batch = await r.json();
        if (Array.isArray(batch)) out = out.concat(batch);
        if (!Array.isArray(batch) || batch.length < PAGE) break;
        from += PAGE;
    }
    return out;
}

// PATCH users.escalation for one phone number.
async function setEscalation(phone, value) {
    const r = await fetch(
        `${SUPABASE_URL}/rest/v1/users?phonenumber=eq.${encodeURIComponent(phone)}`,
        {
            method: "PATCH",
            headers: { ...headers, "Content-Type": "application/json", Prefer: "return=minimal" },
            body: JSON.stringify({ escalation: value }),
        },
    );
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
}

// PATCH conversations.notes + note_author for ONE message row (by id). Empty note
// clears both. Internal-only (never sent to WhatsApp). return=representation so a
// silent 0-row update (RLS with no UPDATE policy on conversations → 200 + []) is
// caught and surfaced instead of looking "saved".
async function setNote(messageId, note, author) {
    const clean = String(note || "").trim();
    const r = await fetch(
        `${SUPABASE_URL}/rest/v1/conversations?id=eq.${encodeURIComponent(messageId)}&select=id,notes,note_author`,
        {
            method: "PATCH",
            headers: { ...headers, "Content-Type": "application/json", Prefer: "return=representation" },
            body: JSON.stringify({
                notes: clean || null,
                note_author: clean ? (author || null) : null,
            }),
        },
    );
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
    const rows = await r.json().catch(() => []);
    if (!Array.isArray(rows) || rows.length === 0) {
        throw new Error("Not saved — 0 rows updated (add an RLS UPDATE policy on conversations)");
    }
    return rows[0];
}

// PATCH users.activity_override for one phone number — the MANUAL Active/Inactive
// pin set from the profile panel. `value` is "active" | "inactive" | null, where
// null means "back to automatic" (the 7-day last_inbound_at rule). The column is
// CHECK-constrained to exactly those three, and a genuine new inbound CLEARS it
// DB-side, so the UI only ever writes an intent and re-reads the result.
async function setActivityOverride(phone, value) {
    const r = await fetch(
        `${SUPABASE_URL}/rest/v1/users?phonenumber=eq.${encodeURIComponent(phone)}`,
        {
            method: "PATCH",
            headers: { ...headers, "Content-Type": "application/json", Prefer: "return=minimal" },
            body: JSON.stringify({ activity_override: value }),
        },
    );
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
}

// PATCH users.assigned_staff_* for one phone number.
async function setAssignedStaff(phone, staff) {
    const r = await fetch(
        `${SUPABASE_URL}/rest/v1/users?phonenumber=eq.${encodeURIComponent(phone)}`,
        {
            method: "PATCH",
            headers: { ...headers, "Content-Type": "application/json", Prefer: "return=minimal" },
            body: JSON.stringify({
                assigned_staff_id: staff ? staff.id : null,
                assigned_staff_email: staff ? staff.email : null,
                assigned_staff_name: staff ? staff.name : null,
            }),
        },
    );
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
}

// PATCH staff.active for one staff row. Needs the staff_anon_update RLS policy;
// return=representation lets us detect a silent 0-row (policy missing) update.
async function setStaffActive(staffId, active) {
    const r = await fetch(
        `${SUPABASE_URL}/rest/v1/staff?id=eq.${encodeURIComponent(staffId)}&select=id,active`,
        {
            method: "PATCH",
            headers: { ...headers, "Content-Type": "application/json", Prefer: "return=representation" },
            body: JSON.stringify({ active }),
        },
    );
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
    const rows = await r.json().catch(() => []);
    if (!Array.isArray(rows) || rows.length === 0) {
        throw new Error("Staff not updated — 0 rows (add the staff_anon_update RLS policy in Supabase)");
    }
}

// Bulk reassign: move ALL of one staff's conversations to another (one filtered PATCH).
async function reassignAllFrom(fromEmail, toStaff) {
    const r = await fetch(
        `${SUPABASE_URL}/rest/v1/users?assigned_staff_email=eq.${encodeURIComponent(fromEmail)}`,
        {
            method: "PATCH",
            headers: { ...headers, "Content-Type": "application/json", Prefer: "return=minimal" },
            body: JSON.stringify({
                assigned_staff_id: toStaff.id,
                assigned_staff_email: toStaff.email,
                assigned_staff_name: toStaff.name,
            }),
        },
    );
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
}

// Reassign EVERY conversation to one staff (a filter that matches all rows —
// PostgREST refuses an unfiltered PATCH, so we scope by phonenumber not null).
async function reassignEveryone(toStaff) {
    const r = await fetch(
        `${SUPABASE_URL}/rest/v1/users?phonenumber=not.is.null`,
        {
            method: "PATCH",
            headers: { ...headers, "Content-Type": "application/json", Prefer: "return=minimal" },
            body: JSON.stringify({
                assigned_staff_id: toStaff.id,
                assigned_staff_email: toStaff.email,
                assigned_staff_name: toStaff.name,
            }),
        },
    );
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
}

// Round-robin redistribute one staff's conversations evenly across `targets`
// (active staff). Fetches their phones, buckets them, PATCHes one group per target.
async function redistributeFrom(fromEmail, targets) {
    if (!targets || !targets.length) throw new Error("no active staff to receive the chats");
    const rows = await supaGet(
        `users?select=phonenumber&assigned_staff_email=eq.${encodeURIComponent(fromEmail)}`,
    );
    const phones = (Array.isArray(rows) ? rows : []).map((u) => u.phonenumber).filter(Boolean);
    if (!phones.length) return;
    const buckets = targets.map(() => []);
    phones.forEach((p, i) => buckets[i % targets.length].push(p));
    for (let i = 0; i < targets.length; i++) {
        const bucket = buckets[i];
        if (!bucket.length) continue;
        const t = targets[i];
        const listEnc = bucket.map((p) => encodeURIComponent(`"${p}"`)).join(",");
        const r = await fetch(
            `${SUPABASE_URL}/rest/v1/users?phonenumber=in.(${listEnc})`,
            {
                method: "PATCH",
                headers: { ...headers, "Content-Type": "application/json", Prefer: "return=minimal" },
                body: JSON.stringify({
                    assigned_staff_id: t.id, assigned_staff_email: t.email, assigned_staff_name: t.name,
                }),
            },
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
    }
}

// Send a human-takeover message through the Worker relay. The Worker delivers it
// to WhatsApp and writes it to `conversations` — we must NOT insert here.
export async function sendViaWorker(phone, message, staffEmail) {
    const r = await fetch(`${WORKER_URL}/agent-send`, {
        method: "POST",
        headers: { "x-agent-secret": AGENT_SECRET, "content-type": "application/json" },
        body: JSON.stringify({ phonenumber: phone, message, staff_email: staffEmail || "" }),
    });
    if (!r.ok) throw new Error(`Worker HTTP ${r.status}: ${await r.text()}`);
}

// ── time helpers ───────────────────────────────────────────────
const TZ = "Asia/Singapore";
function timeOfDay(iso) {
    try {
        return new Date(iso).toLocaleTimeString("en-SG", {
            hour: "2-digit", minute: "2-digit", hour12: false, timeZone: TZ,
        });
    } catch { return ""; }
}
function dayLabel(iso) {
    try {
        const d = new Date(iso);
        const today = new Date();
        const yday = new Date(); yday.setDate(today.getDate() - 1);
        const sameDay = (a, b) =>
            a.toLocaleDateString("en-SG", { timeZone: TZ }) ===
            b.toLocaleDateString("en-SG", { timeZone: TZ });
        if (sameDay(d, today)) return "Today";
        if (sameDay(d, yday)) return "Yesterday";
        return d.toLocaleDateString("en-SG", {
            day: "2-digit", month: "short", year: "numeric", timeZone: TZ,
        });
    } catch { return ""; }
}
// Relative-ish sidebar stamp: HH:MM if today, else the day label.
function sidebarStamp(iso) {
    const dl = dayLabel(iso);
    return dl === "Today" ? timeOfDay(iso) : dl;
}

// ── author attribution ─────────────────────────────────────────
// prospect → incoming customer (is_user_message=true / sender='prospect')
// staff    → human takeover reply (sender='staff' or staff_email stamped)
// bot      → AI / automated send (everything else outgoing)
function messageAuthor(m) {
    if (m.sender === "staff" || (m.staff_email && String(m.staff_email).trim())) return "staff";
    if (m.sender === "prospect" || m.is_user_message) return "prospect";
    return "bot";
}

function initialsOf(name, phone) {
    if (name && name.trim()) {
        const p = name.trim().split(/\s+/);
        return (p[0][0] + (p[1]?.[0] || "")).toUpperCase();
    }
    return (phone || "?").slice(-2);
}

// Stable accent colour per phone (hash → palette). Keeps avatars colourful
// without a DB column for it.
// One calm, on-brand avatar tone for everyone — no rainbow. Initials still
// differentiate people; colour stays quiet so the inbox reads simple.
const AVATAR_TONE = "#6b8f89";
function accentFor(_key) {
    return AVATAR_TONE;
}

// Stable staff colour for the roster mapping (STAFF shape {id,name,colour}).
function staffColour(key) {
    return accentFor("staff:" + key);
}

// ── image messages (load-on-demand + session cache) ─────────────
// Image rows aren't fetched automatically. The bubble shows a Load button; on
// click we POST the media reference (message_body) to the worker's /media proxy,
// which returns/streams the real image. The resolved src is cached in-memory for
// the browser session (survives chat-switches + the 5s poll), so each image hits
// the worker at most once until the tab is closed/reloaded.
const mediaCache = new Map(); // mediaRef -> displayable image src (URL / object URL / data URL)

export function getCachedMedia(ref) {
    return ref ? mediaCache.get(ref) || null : null;
}

export async function fetchMedia(ref) {
    if (!ref) throw new Error("no media reference");
    if (mediaCache.has(ref)) return mediaCache.get(ref);

    if (!WORKER_URL || !AGENT_SECRET) throw new Error("Worker not configured");
    // Worker /media proxy (see dev doc): POST { media_id } with the shared secret;
    // 200 → raw image bytes (real Content-Type, Cache-Control private 24h). We
    // blob it to an object URL and cache module-side so the poll never re-fetches.
    // ref is the WhatsApp media-id stored in message_body on the format='image' row.
    const r = await fetch(`${WORKER_URL}/media`, {
        method: "POST",
        headers: { "content-type": "application/json", "x-agent-secret": AGENT_SECRET },
        body: JSON.stringify({ media_id: ref }),
    });
    if (r.status === 404) throw new Error("expired");   // WhatsApp keeps media ~30 days
    if (!r.ok) throw new Error(`Worker /media HTTP ${r.status}`);
    const src = URL.createObjectURL(await r.blob());
    mediaCache.set(ref, src);
    return src;
}

// Fetch ONE conversation's full message rows (oldest→newest) — used by the JSON
// export to include complete transcripts. One conversation is well under the 1000-row cap.
export async function fetchThreadRows(phone) {
    if (!phone) return [];
    const rows = await supaGet(
        "conversations?select=id,is_user_message,message_body,created_at,sender,staff_email,format,notes,note_author,wa_id,context_wa_id" +
        `&phonenumber=eq.${encodeURIComponent(phone)}&order=created_at.asc`,
    );
    return Array.isArray(rows) ? rows : [];
}

// ── thread building ─────────────────────────────────────────────
// Group ONE conversation's messages (chronological) into day sections for the UI.
// Shared by buildState (the light sidebar poll) and loadThread (the per-conversation
// full-history fetch when a chat is opened).
function groupThread(msgs, u = {}, staffByEmail = new Map()) {
    const sorted = [...msgs].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    // Quoted-reply lookup: wa_id → row, within THIS conversation only. Older rows
    // never got a wa_id stamped, so they simply never match — fail-open, the reply
    // renders as a plain bubble with no quote block.
    const byWaId = new Map();
    for (const m of sorted) {
        if (m && m.wa_id) byWaId.set(m.wa_id, m);
    }
    const groups = [];
    let lastDay = null;
    for (const m of sorted) {
        const dl = dayLabel(m.created_at);
        if (dl !== lastDay) { groups.push({ day: dl, items: [] }); lastDay = dl; }
        const by = messageAuthor(m);
        let staffName = null;
        if (by === "staff") {
            const email = (m.staff_email || "").trim().toLowerCase();
            staffName = (email && staffByEmail.get(email)) || email || u.assigned_staff_name || "Staff";
        }
        const isImage = m.format === "image";
        // Quoted reply (WhatsApp "reply to"): context_wa_id points at the wa_id of
        // the quoted message. Resolve it in the same conversation; if it can't be
        // found (older message with no stored wa_id) leave quoted=null and render
        // the bubble exactly as today. Never throws, never blocks the bubble.
        let quoted = null;
        if (m.context_wa_id) {
            const q = byWaId.get(m.context_wa_id);
            if (q) {
                const qBy = messageAuthor(q);
                let qStaff = null;
                if (qBy === "staff") {
                    const qEmail = (q.staff_email || "").trim().toLowerCase();
                    qStaff = (qEmail && staffByEmail.get(qEmail)) || qEmail || u.assigned_staff_name || "Staff";
                }
                quoted = {
                    id: q.id, // the quoted message's row id → click-to-scroll target
                    by: qBy,
                    staff: qStaff,
                    text: q.format === "image" ? "Photo" : (q.message_body || ""),
                };
            }
        }
        groups[groups.length - 1].items.push({
            id: m.id,
            by,
            staff: staffName,
            // Image rows carry the media reference in message_body (a WhatsApp media
            // id/URL). We DON'T fetch it here — the bubble shows a Load button and
            // fetches via the worker /media proxy on demand.
            format: isImage ? "image" : "text",
            mediaRef: isImage ? m.message_body : null,
            text: isImage ? "" : (m.message_body || ""),
            quoted,
            note: m.notes || null,
            noteBy: m.note_author || null,
            time: timeOfDay(m.created_at),
        });
    }
    return groups;
}

// Roll the flat conversation rows into one thread per phonenumber, then map into
// the three UI shapes the new App consumes.
// Per-conversation "last seen" marker for unread counts, persisted in localStorage.
const SEEN_KEY = "inbox_seen_v1";
function readSeen() {
    try { return JSON.parse(localStorage.getItem(SEEN_KEY)) || {}; } catch { return {}; }
}
function writeSeen(map) {
    try { localStorage.setItem(SEEN_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

// Digits-only phone key so conversations↔users join despite "+"/format differences
// (the +E.164-vs-bare mismatch). Without this the users join silently fails and
// name/source/assigned come back empty even though the DB has them.
const digitsKey = (p) => String(p || "").replace(/\D/g, "");

function buildState(messages, users, staffRoster, seen = {}) {
    // users keyed by NORMALIZED phone (digits only).
    const byPhoneUser = new Map();
    for (const u of users) { const k = digitsKey(u.phonenumber); if (k) byPhoneUser.set(k, u); }

    // staff email → display name, for attributing human-takeover messages.
    const staffByEmail = new Map();
    for (const s of staffRoster) {
        if (s.email) staffByEmail.set(String(s.email).trim().toLowerCase(), s.name || s.email);
    }

    // messages grouped by NORMALIZED phone; keep the ORIGINAL phonenumber (the
    // conversations-table format) so loadThread/agent-send query the right value.
    const byPhone = new Map(); // digitsKey -> { phone, msgs }
    for (const m of messages) {
        if (!m || !m.phonenumber) continue;
        const k = digitsKey(m.phonenumber);
        if (!k) continue;
        if (!byPhone.has(k)) byPhone.set(k, { phone: m.phonenumber, msgs: [] });
        byPhone.get(k).msgs.push(m);
    }

    const conversations = [];
    const threads = {};
    const profiles = {};

    // One conversation per prospect from the UNION of the users table (the
    // authoritative list — every prospect, no 1000-row message cap) and any phone
    // seen in the (capped) message poll. Dormant chats whose latest message predates
    // the message window still appear; their full history loads on open via loadThread.
    const keys = new Set([...byPhone.keys(), ...byPhoneUser.keys()]);

    for (const k of keys) {
        const bucket = byPhone.get(k);
        const u = byPhoneUser.get(k) || {};
        const msgs = bucket ? bucket.msgs.slice().sort((a, b) => new Date(a.created_at) - new Date(b.created_at)) : [];
        // Conversation phone/id: the message phone when we have messages (matches the
        // conversations table for loadThread); otherwise the user's own phone.
        const phone = bucket ? bucket.phone : u.phonenumber;
        const last = msgs.length ? msgs[msgs.length - 1] : null;
        // Unread = prospect messages newer than the last-seen marker for this phone.
        // No marker yet (never sighted) → 0; loadOnce baselines it right after.
        const seenAt = seen[phone];
        const unread = seenAt == null
            ? 0
            : msgs.filter((m) => messageAuthor(m) === "prospect" && new Date(m.created_at).getTime() > seenAt).length;
        // Engagement: has the prospect ever replied, and their latest inbound time?
        // Drives the sort (engaged leads to the top; not-yet-replied to the bottom).
        let lastInboundAt = 0;
        for (const m of msgs) {
            if (messageAuthor(m) === "prospect") {
                const t = new Date(m.created_at).getTime();
                if (t > lastInboundAt) lastInboundAt = t;
            }
        }
        const hasReplied = lastInboundAt > 0;
        const name =
            u.name ||
            ([u.first_name, u.last_name].filter(Boolean).join(" ").trim() || null);
        const accent = accentFor(phone);

        // CONVERSATIONS row
        conversations.push({
            id: phone,
            name,
            phone,
            initials: initialsOf(name, phone),
            time: last ? sidebarStamp(last.created_at) : "",
            preview: last?.format === "image" ? "Photo" : (last?.message_body || ""),
            previewBy: last ? messageAuthor(last) : "prospect",
            unread,
            zoho: Boolean(u.lead_url),
            escalated: Boolean(u.escalation),
            assigned: u.assigned_staff_email || null,
            assignedName: u.assigned_staff_name || null,
            source: u.source || null,
            // LEAD ACTIVITY BADGE (`+leadactive`). Active = the lead sent us a genuine inbound
            // within 7 days. Read from `users.last_inbound_at` (db/72), which a DB trigger keeps
            // current on every real inbound — NOT from the local `lastInboundAt` above, which only
            // sees the fetched message window (MSG_LIMIT) and would call an old lead inactive purely
            // because their messages fell out of that window.
            // Undefined column (older DB, or the no-source fallback fetch) => `null`, which the
            // filter treats as "unknown" and never hides. Absent must not mean inactive.
            leadInboundAt: u.last_inbound_at ? new Date(u.last_inbound_at).getTime() : 0,
            ...activityOf(u),
            accent,
            lastAt: last ? new Date(last.created_at).getTime() : 0,
            lastInboundAt,
            hasReplied,
            // Inbound/"came-in" date = users.created_at (every row has it). This is
            // what the calendar range-filter reads, so it works for ALL leads.
            inboundAt: u.created_at ? new Date(u.created_at).getTime() : (last ? new Date(last.created_at).getTime() : 0),
        });

        // THREADS[phone] — messages grouped into day sections (via the shared helper)
        threads[phone] = groupThread(msgs, u, staffByEmail);

        // PROFILE[phone]
        profiles[phone] = {
            name: name || phone,
            firstName: u.first_name || null,
            lastName: u.last_name || null,
            email: u.email || null,
            phone,
            budget: u.budget || null,
            area: u.neighbourhoods || null,
            moveIn: u.move_in_date || null,
            occupancy: u.occupancy || null,
            stay: u.stay_length || null,
            zohoId: u.lead_url || null,
            source: u.source || null,
        };
    }

    // Engagement sort: leads the prospect has REPLIED to float to the top (newest
    // inbound first); not-yet-replied leads sink to the bottom (by last activity),
    // so the list surfaces conversations that actually need attention.
    conversations.sort((a, b) => {
        if (a.hasReplied !== b.hasReplied) return a.hasReplied ? -1 : 1;
        if (a.hasReplied) return b.lastInboundAt - a.lastInboundAt;
        return b.lastAt - a.lastAt;
    });

    // STAFF roster mapped to the UI shape {id,name,colour}; id = email.
    // Union the active roster with any assigned staff seen in users (covers
    // admins removed from the active roster but still attached to a lead).
    const staffMap = new Map();
    for (const s of staffRoster) {
        if (s.active === false) continue; // assignable roster = active staff only
        const id = (s.email || s.id || "").toString().trim().toLowerCase();
        if (!id) continue;
        staffMap.set(id, {
            id,
            name: s.name || s.email || id,
            email: s.email || id,
            staffId: s.id,
            colour: staffColour(id),
        });
    }
    for (const u of users) {
        const id = (u.assigned_staff_email || "").trim().toLowerCase();
        if (id && !staffMap.has(id)) {
            staffMap.set(id, {
                id,
                name: u.assigned_staff_name || id,
                email: u.assigned_staff_email,
                staffId: u.assigned_staff_id,
                colour: staffColour(id),
            });
        }
    }
    const staff = [...staffMap.values()].sort((a, b) => a.name.localeCompare(b.name));

    // Full staff list (active + inactive) for the Manage-Staff panel, with a live
    // count of how many conversations are currently assigned to each.
    const countByEmail = new Map();
    for (const c of conversations) {
        if (c.assigned) {
            const k = c.assigned.toLowerCase();
            countByEmail.set(k, (countByEmail.get(k) || 0) + 1);
        }
    }
    const staffAll = (staffRoster || []).map((s) => ({
        id: s.id,
        name: s.name || s.email || String(s.id),
        email: s.email || null,
        active: s.active !== false,
        sortOrder: s.sort_order ?? 0,
        chatCount: s.email ? (countByEmail.get(String(s.email).toLowerCase()) || 0) : 0,
    })).sort((a, b) => (a.sortOrder - b.sortOrder) || a.name.localeCompare(b.name));

    return { conversations, threads, profiles, staff, staffAll };
}

const EMPTY = { conversations: [], threads: {}, profiles: {}, staff: [], staffAll: [] };

// useInboxData — the live data hook. Returns mapped state + actions.
export function useInboxData() {
    const [data, setData] = useState(EMPTY);
    const [threads, setThreads] = useState({}); // phone -> day-grouped full history (per-conversation, on open)
    const [error, setError] = useState("");
    const [lastSync, setLastSync] = useState(null);
    const aliveRef = useRef(true);
    const staffMapRef = useRef(new Map()); // email -> display name, refreshed each poll (for loadThread)
    // phone -> intended escalation value while a write is in flight, so the poll
    // can't revert an optimistic flip before the DB write is visible.
    const pendingEscRef = useRef(new Map());
    // Same idea for the manual activity pin: phone -> intended activity_override
    // ("active" | "inactive" | null) while the PATCH is in flight, so the 5s poll
    // can't flicker the profile panel back to the old value.
    const pendingActivityRef = useRef(new Map());

    const loadOnce = useCallback(async () => {
        const [messages, users, roster] = await Promise.all([
            supaGet(
                // SIDEBAR poll — LIGHTWEIGHT: no message_body/notes (the big fields). This poll only
                // builds the conversation list + recency + escalation colour-coding. The full message
                // text for an OPEN conversation is fetched per-conversation by loadThread() below. This
                // is the main egress cut (message bodies were ~1000 rows of text pulled every 5s).
                "conversations?select=phonenumber,is_user_message,created_at,sender,format" +
                `&order=created_at.desc&limit=${MSG_LIMIT}`,
            ),
            // Widest select first, then STEP DOWN one column-group at a time, so a DB
            // that is only partly migrated keeps every feature it can actually support
            // instead of losing all of them at once. PostgREST 400s the whole request on
            // one unknown column, so each tier drops exactly one group:
            //   1. + activity_override  (manual Active/Inactive pin, profile panel)
            //   2. + last_inbound_at    (db/72 — the 7-day activity rule + the filter)
            //   3. + source             (db/38 — source colours + the source dropdown)
            //   4. base                 (always present)
            // A column missing from the response is `undefined`, which activityOf() reads
            // as UNKNOWN — never as "inactive". supaGetAll + a stable order → ALL users,
            // not just the first 1000 (PostgREST cap); otherwise the newest ~38 leads
            // render null source / "Unassigned" (+usersallpages).
            supaGetAll(
                "users?select=phonenumber,name,escalation,lead_url,assigned_staff_id,assigned_staff_email,assigned_staff_name,email,first_name,last_name,move_in_date,budget,occupancy,neighbourhoods,stay_length,created_at,source,last_inbound_at,activity_override&order=phonenumber.asc",
            ).catch(() => supaGetAll(
                "users?select=phonenumber,name,escalation,lead_url,assigned_staff_id,assigned_staff_email,assigned_staff_name,email,first_name,last_name,move_in_date,budget,occupancy,neighbourhoods,stay_length,created_at,source,last_inbound_at&order=phonenumber.asc",
            )).catch(() => supaGetAll(
                "users?select=phonenumber,name,escalation,lead_url,assigned_staff_id,assigned_staff_email,assigned_staff_name,email,first_name,last_name,move_in_date,budget,occupancy,neighbourhoods,stay_length,created_at,source&order=phonenumber.asc",
            )).catch(() => supaGetAll(
                "users?select=phonenumber,name,escalation,lead_url,assigned_staff_id,assigned_staff_email,assigned_staff_name,email,first_name,last_name,move_in_date,budget,occupancy,neighbourhoods,stay_length,created_at&order=phonenumber.asc",
            )),
            // ALL staff (active + inactive) with the flag, for the roster + management.
            supaGet("staff?select=id,name,email,active,sort_order&order=sort_order.asc")
                .catch(() => supaGet("staff?select=id,name,email").catch(() => [])),
        ]);
        if (!aliveRef.current) return;
        const seen = readSeen();
        const built = buildState(
            Array.isArray(messages) ? messages : [],
            Array.isArray(users) ? users : [],
            Array.isArray(roster) ? roster : [],
            seen,
        );
        // Baseline any conversation we're seeing for the first time to its latest
        // message, so pre-existing history never shows as unread — only messages
        // that arrive AFTER this count. (Opening a chat later re-marks it.)
        let seenChanged = false;
        for (const c of built.conversations) {
            if (seen[c.id] == null) { seen[c.id] = c.lastAt; seenChanged = true; }
        }
        if (seenChanged) writeSeen(seen);
        // Respect an in-flight activity pin until the DB catches up.
        if (pendingActivityRef.current.size) {
            const dbOv = new Map((users || []).map((u) => [u.phonenumber, u.activity_override || null]));
            for (const c of built.conversations) {
                if (!pendingActivityRef.current.has(c.phone)) continue;
                const pend = pendingActivityRef.current.get(c.phone);
                // A genuine new inbound CLEARS the override DB-side. If the DB has
                // dropped a pin we asked for, that's the trigger doing its job — accept
                // the DB and drop our intent rather than fighting it back on.
                if (dbOv.get(c.phone) === pend || (pend && dbOv.get(c.phone) === null && c.activeLead === true)) {
                    pendingActivityRef.current.delete(c.phone);
                } else {
                    // Re-apply our intent on top of the row's own 7-day derivation.
                    // (Never re-derive from leadInboundAt: 0 there is ambiguous between
                    // "never messaged" and "column absent", and the second must stay null.)
                    Object.assign(c, {
                        activityOverride: pend,
                        activityIsManual: Boolean(pend),
                        activeLead: pend === "active" ? true
                            : pend === "inactive" ? false
                            : c.activityDerived,
                    });
                }
            }
        }
        // Respect in-flight toggle intents until the DB catches up.
        if (pendingEscRef.current.size) {
            const dbEsc = new Map((users || []).map((u) => [u.phonenumber, Boolean(u.escalation)]));
            for (const c of built.conversations) {
                const pend = pendingEscRef.current.get(c.phone);
                if (pend === undefined) continue;
                if (dbEsc.get(c.phone) === pend) pendingEscRef.current.delete(c.phone);
                else c.escalated = pend;
            }
        }
        setData(built);
        staffMapRef.current = new Map((built.staff || []).map((s) => [s.id, s.name]));
        setError("");
        setLastSync(Date.now());
    }, []);

    // Fetch ONE conversation's FULL history (by phone) and store it in `threads`. Called when a chat
    // is opened (and re-polled while open, for live updates during human takeover). This is what shows
    // the complete conversation — the sidebar poll above no longer carries message bodies, and a single
    // conversation is tiny so the 1000-row PostgREST cap never truncates it. Fail-open.
    const loadThread = useCallback(async (phone) => {
        if (!phone) return;
        try {
            const rows = await supaGet(
                "conversations?select=id,is_user_message,message_body,created_at,sender,staff_email,format,notes,note_author,wa_id,context_wa_id" +
                `&phonenumber=eq.${encodeURIComponent(phone)}&order=created_at.asc`,
            );
            if (!aliveRef.current || !Array.isArray(rows)) return;
            const groups = groupThread(rows, {}, staffMapRef.current);
            setThreads((t) => ({ ...t, [phone]: groups }));
        } catch (e) {
            if (aliveRef.current) setError(String(e.message || e));
        }
    }, []);

    useEffect(() => {
        if (!CONFIGURED) {
            setError("Supabase not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in .env");
            return;
        }
        aliveRef.current = true;
        const load = () => loadOnce().catch((e) => {
            if (aliveRef.current) setError(String(e.message || e));
        });
        load();
        const id = setInterval(load, REFRESH_MS);
        return () => { aliveRef.current = false; clearInterval(id); };
    }, [loadOnce]);

    // Optimistic escalation toggle.
    const toggleEscalation = useCallback(async (phone, current) => {
        if (!isAnswerable(phone)) { setError(BLOCKED_MSG); throw new Error(BLOCKED_MSG); }
        const cur = pendingEscRef.current.has(phone)
            ? pendingEscRef.current.get(phone)
            : current;
        const next = !cur;
        pendingEscRef.current.set(phone, next);
        setData((d) => ({
            ...d,
            conversations: d.conversations.map((c) =>
                c.phone === phone ? { ...c, escalated: next } : c),
        }));
        try {
            await setEscalation(phone, next);
            await loadOnce();
        } catch (e) {
            pendingEscRef.current.delete(phone);
            setData((d) => ({
                ...d,
                conversations: d.conversations.map((c) =>
                    c.phone === phone ? { ...c, escalated: cur } : c),
            }));
            setError(String(e.message || e));
            throw e;
        }
    }, [loadOnce]);

    // Set (or clear) the MANUAL activity pin for one lead, from the profile panel.
    // `value` is "active" | "inactive" | null, null meaning back to automatic.
    // Optimistic + an in-flight intent, like toggleEscalation, so the 5s poll can't
    // flicker the control back. Fails SOFT: if the activity_override column isn't
    // there yet the PATCH 400s, we roll the row back and surface the error — the
    // panel keeps working, it just can't pin.
    const setLeadActivity = useCallback(async (phone, value) => {
        // TEST-MODE GUARD. Same rule as toggleEscalation and sendReply: in local mode only a
        // whitelisted number may be written to, so a dev poking around the inbox cannot change the
        // state of a real customer's lead. This was missing when the control was first built.
        if (!isAnswerable(phone)) { setError(BLOCKED_MSG); throw new Error(BLOCKED_MSG); }
        const next = value === "active" || value === "inactive" ? value : null;
        const prev = new Map();
        pendingActivityRef.current.set(phone, next);
        setData((d) => ({
            ...d,
            conversations: d.conversations.map((c) => {
                if (c.phone !== phone) return c;
                prev.set(phone, {
                    activityOverride: c.activityOverride,
                    activityIsManual: c.activityIsManual,
                    activeLead: c.activeLead,
                });
                return {
                    ...c,
                    activityOverride: next,
                    activityIsManual: Boolean(next),
                    activeLead: next === "active" ? true
                        : next === "inactive" ? false
                        : c.activityDerived,
                };
            }),
        }));
        try {
            await setActivityOverride(phone, next);
            await loadOnce();
        } catch (e) {
            pendingActivityRef.current.delete(phone);
            const back = prev.get(phone);
            if (back) {
                setData((d) => ({
                    ...d,
                    conversations: d.conversations.map((c) =>
                        c.phone === phone ? { ...c, ...back } : c),
                }));
            }
            setError(String(e.message || e));
            throw e;
        }
    }, [loadOnce]);

    // Optimistic reassign. `staff` is a STAFF row {id,email,name,staffId} or null.
    const reassign = useCallback(async (phone, staff) => {
        setData((d) => ({
            ...d,
            conversations: d.conversations.map((c) =>
                c.phone === phone
                    ? { ...c, assigned: staff ? staff.email : null, assignedName: staff ? staff.name : null }
                    : c),
        }));
        try {
            await setAssignedStaff(phone, staff ? { id: staff.staffId, email: staff.email, name: staff.name } : null);
            await loadOnce();
        } catch (e) {
            setError(String(e.message || e));
            await loadOnce().catch(() => {});
            throw e;
        }
    }, [loadOnce]);

    // Send a human reply via the Worker relay, then refresh.
    const sendReply = useCallback(async (phone, text, staffEmail) => {
        if (!isAnswerable(phone)) { setError(BLOCKED_MSG); throw new Error(BLOCKED_MSG); }
        if (!CAN_SEND) {
            const msg = "Worker not configured. Set REACT_APP_WORKER_URL and REACT_APP_AGENT_SECRET in .env";
            setError(msg);
            throw new Error(msg);
        }
        await sendViaWorker(phone, text, staffEmail);
        await loadOnce();
    }, [loadOnce]);

    // Save/edit/clear a staff note on one message row (real Supabase write), then
    // refresh. Internal only — not gated by test mode (notes never reach WhatsApp).
    const saveNote = useCallback(async (messageId, note, author) => {
        await setNote(messageId, note, author);
        await loadOnce();
    }, [loadOnce]);

    // Mark a conversation read up to `lastAt` (ms): persist the seen marker and clear
    // its unread badge now (the poll then keeps it 0 until a newer prospect message).
    const markSeen = useCallback((phone, lastAt) => {
        if (!phone) return;
        const seen = readSeen();
        const at = lastAt || Date.now();
        if (seen[phone] === at) return;
        seen[phone] = at;
        writeSeen(seen);
        setData((d) => ({
            ...d,
            conversations: d.conversations.map((c) => (c.phone === phone ? { ...c, unread: 0 } : c)),
        }));
    }, []);

    // ── Manage-staff actions (all refresh the poll on success) ──────────
    // Bulk-move every conversation from one staff to another.
    const bulkReassign = useCallback(async (fromEmail, toStaff) => {
        await reassignAllFrom(fromEmail, toStaff);
        await loadOnce();
    }, [loadOnce]);
    // Move ALL conversations to one staff.
    const reassignAll = useCallback(async (toStaff) => {
        await reassignEveryone(toStaff);
        await loadOnce();
    }, [loadOnce]);
    // Round-robin a staff's conversations across the given active targets.
    const redistribute = useCallback(async (fromEmail, targets) => {
        await redistributeFrom(fromEmail, targets);
        await loadOnce();
    }, [loadOnce]);
    // Toggle a staff active/inactive.
    const setActive = useCallback(async (staffId, active) => {
        await setStaffActive(staffId, active);
        await loadOnce();
    }, [loadOnce]);

    return { data, threads, loadThread, error, lastSync, loadOnce, toggleEscalation, setLeadActivity, reassign, sendReply, saveNote, markSeen, bulkReassign, reassignAll, redistribute, setActive };
}
